import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from 'components/Loading';
import { useClients } from 'hooks';
import { useFirstAndLastClosureDates } from 'utils/closureHooks';
import {
  downloadCharacterizationAverageReport,
  downloadClientExpeditionConfirmationReport,
  downloadClientExpeditionReport,
  downloadClientExpeditionValidatedReport,
  downloadClientMonthlyResultReport,
  downloadCumulatedPassageChaine,
} from 'services/apiOperations';
import { DefaultExpeditionClientFormValues, DefaultFormValues, ExpeditionClientReportForm, ReportForm } from '../types';
import DownloadReportCard from '../components/DownloadReportCard';
import DownloadExpeditionReportCard from '../components/DownloadExpeditionReportCard';
import { monthlyResultClientReport } from './MonthlyResultClientReport.yup';
import { expeditionClientReport } from './ExpeditionClientReport.yup';
import { characterizationAverageReport } from './CharacterizationAverageReport.yup';
import { cumulatedPassageChaineReport } from './cumulatedPassageChaineReport';
import { DEFAULT_ITEMS_PER_SELECT } from '../../../tableFilterContext';

const ClientReports: React.FC = () => {
  const closedMonthsBoundaries = useFirstAndLastClosureDates();

  const [clients] = useClients(DEFAULT_ITEMS_PER_SELECT);
  const { t } = useTranslation('translation', { keyPrefix: 'reports' });

  const monthlyResultClientReportSchema = useMemo(() => monthlyResultClientReport(t), [t]);
  const characterizationAverageReportSchema = useMemo(() => characterizationAverageReport(t), [t]);
  const cumulatedPassageChaineReportSchema = useMemo(() => cumulatedPassageChaineReport(t), [t]);
  const expeditionClientReportSchema = useMemo(() => expeditionClientReport(t), [t]);

  const defaultDate = closedMonthsBoundaries ? closedMonthsBoundaries.lastClosureDate : null;
  const defaultFormValue: DefaultFormValues = {
    year: defaultDate,
    month: defaultDate,
    client: '',
  };
  const defaultCharacterizationAverageFormValue: DefaultFormValues = {
    year: defaultDate,
    month: defaultDate,
  };
  const defaultCumulatedPassageChaineFormValue: DefaultFormValues = {
    year: defaultDate,
    month: defaultDate,
  };
  const defaultExpeditionWeightFormValue: DefaultExpeditionClientFormValues = {
    startDate: null,
    endDate: null,
  };

  const downloadMonthlyResultReport = (sortingCenterId: string, form: ReportForm): Promise<void> => {
    if (!form.client) return Promise.resolve();

    return downloadClientMonthlyResultReport(
      sortingCenterId,
      form.year.toLocaleString('default', { year: 'numeric' }),
      form.month.toLocaleString('default', { month: 'numeric' }),
      form.client,
      false,
    );
  };

  const handleDownloadCharacterizationAverageReport = (sortingCenterId: string, form: ReportForm): Promise<void> => {
    return downloadCharacterizationAverageReport(
      sortingCenterId,
      form.year.toLocaleString('default', { year: 'numeric' }),
      form.month.toLocaleString('default', { month: 'numeric' }),
      false,
    );
  };

  const handleDownloadCumulatedPassageChaine = (sortingCenterId: string, form: ReportForm): Promise<void> => {
    return downloadCumulatedPassageChaine(
      sortingCenterId,
      form.year.toLocaleString('default', { year: 'numeric' }),
      form.month.toLocaleString('default', { month: 'numeric' }),
      false,
    );
  };

  const downloadExpeditionReport = (sortingCenterId: string, form: ExpeditionClientReportForm): Promise<void> => {
    return downloadClientExpeditionReport(sortingCenterId, form.startDate, form.endDate);
  };

  const downloadExpeditionConfirmationReport = (
    sortingCenterId: string,
    form: ExpeditionClientReportForm,
  ): Promise<void> => {
    return downloadClientExpeditionConfirmationReport(sortingCenterId, form.startDate, form.endDate);
  };

  const downloadExpeditionValidatedReport = (
    sortingCenterId: string,
    form: ExpeditionClientReportForm,
  ): Promise<void> => {
    return downloadClientExpeditionValidatedReport(sortingCenterId, form.startDate, form.endDate);
  };

  return (
    <>
      {clients ? (
        <>
          <DownloadReportCard
            collapseTitle={t('client.monthlyResult')}
            formId={'monthlyResult_client_report_form'}
            handleClickDownload={downloadMonthlyResultReport}
            selectClient
            closedMonthsBoundaries={closedMonthsBoundaries}
            clients={clients}
            yupSchemaShape={monthlyResultClientReportSchema}
            defaultFormValue={defaultFormValue}
          />
          <DownloadExpeditionReportCard
            collapseTitle={t('client.expeditionClient.title')}
            formId={'expedition_client_report_form'}
            handleClickDownload={downloadExpeditionReport}
            yupSchemaShape={expeditionClientReportSchema}
            defaultFormValue={defaultExpeditionWeightFormValue}
          />
          <DownloadExpeditionReportCard
            collapseTitle={t('client.expeditionConfirmationClient.title')}
            formId={'expedition_confirmation_client_report_form'}
            handleClickDownload={downloadExpeditionConfirmationReport}
            yupSchemaShape={expeditionClientReportSchema}
            defaultFormValue={defaultExpeditionWeightFormValue}
          />
          <DownloadExpeditionReportCard
            collapseTitle={t('client.expeditionValidatedClient.title')}
            formId={'expedition_Validated_client_report_form'}
            handleClickDownload={downloadExpeditionValidatedReport}
            yupSchemaShape={expeditionClientReportSchema}
            defaultFormValue={defaultExpeditionWeightFormValue}
          />
          <DownloadReportCard
            collapseTitle={t('client.characterizationAverage.title')}
            formId={'characterizationAverage_report_form'}
            handleClickDownload={handleDownloadCharacterizationAverageReport}
            selectClient={false}
            closedMonthsBoundaries={closedMonthsBoundaries}
            yupSchemaShape={characterizationAverageReportSchema}
            defaultFormValue={defaultCharacterizationAverageFormValue}
          />
          <DownloadReportCard
            collapseTitle={t('client.cumulatedPassageChaine.title')}
            formId={'cumulatedPassageChaine_report_form'}
            handleClickDownload={handleDownloadCumulatedPassageChaine}
            selectClient={false}
            closedMonthsBoundaries={closedMonthsBoundaries}
            yupSchemaShape={cumulatedPassageChaineReportSchema}
            defaultFormValue={defaultCumulatedPassageChaineFormValue}
          />
        </>
      ) : (
        <Loading />
      )}
    </>
  );
};
export default ClientReports;
